import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import './ChatAIMessageList.scss';

interface ChatAIMessageListProps {
    children: ReactNode;
    /** Смена значения прокручивает к последнему сообщению, даже если читатель ушёл вверх по истории. */
    bottomKey?: string | number;
}

/* Насколько можно не дойти до низа и всё ещё «следить» за новыми сообщениями: дробный scrollTop при масштабе. */
const STICK_GAP_PX = 2;

/**
 * Лента сообщений. Пока читатель внизу, новые сообщения, подгруженные картинки и сжатие окна держат низ на виду;
 * ушёл вверх по истории — позиция сохраняется, пока не сменится `bottomKey`.
 */
export function ChatAIMessageList({ children, bottomKey }: ChatAIMessageListProps) {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const stuck = useRef(true);
    const lastScrollTop = useRef(0);

    const scrollToBottom = () => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        scroller.scrollTop = scroller.scrollHeight;
        lastScrollTop.current = scroller.scrollTop;
    };

    useLayoutEffect(() => {
        stuck.current = true;
        scrollToBottom();
    }, [bottomKey]);

    useLayoutEffect(() => {
        if (stuck.current) scrollToBottom();
    });

    useEffect(() => {
        const scroller = scrollerRef.current;
        const content = contentRef.current;
        if (!scroller || !content || typeof ResizeObserver !== 'function') return;
        const observer = new ResizeObserver(() => {
            if (stuck.current) scrollToBottom();
        });
        observer.observe(scroller);
        observer.observe(content);
        return () => observer.disconnect();
    }, []);

    /* Отлипает только прокрутка вверх: событие от своей прокрутки вниз приходит кадром позже, и если контент за
       этот кадр вырос, низ уже не виден — но читатель никуда не уходил. */
    const handleScroll = () => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const { scrollTop } = scroller;
        if (scroller.scrollHeight - scrollTop - scroller.clientHeight <= STICK_GAP_PX) stuck.current = true;
        else if (scrollTop < lastScrollTop.current) stuck.current = false;
        lastScrollTop.current = scrollTop;
    };

    return (
        <div
            ref={scrollerRef}
            className="chat-ai-message-list"
            role="log"
            aria-label="Сообщения"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Scrollable region must support keyboard scrolling.
            tabIndex={0}
            onScroll={handleScroll}
        >
            <div ref={contentRef} className="chat-ai-message-list__content">
                {children}
            </div>
        </div>
    );
}
